import {
  computeCurrentStage,
  computeDaysOverdue,
  currentPeriodDueDate,
} from "./rent-reminder-templates";

/**
 * COL-472: what a tenant owes once Mahngebühren are counted.
 *
 * `bookings.accumulated_fees_eur` is one fee pool per tenant, never part of any
 * month's `amount_cents`. The reminders surfaces already add it in (see
 * `placeBookingFees`: EUR 350 rent + EUR 40 fees = EUR 390), while the tenant
 * dashboard and the admin tenant profile showed the rent alone — so the same
 * tenant owed EUR 390 on one screen and EUR 350 on the next.
 *
 * On the profile surfaces the pool is shown on ONE month: the earliest open
 * rent month, which is the one the dunning ladder charged those fees against.
 * Totals ("Outstanding") add the pool once, regardless of which row carries it.
 *
 * Card / SEPA checkout still collects rent only (`create-rent-payment` knows
 * nothing about fees); this module only changes what is DISPLAYED as owed.
 */

/** The accrued fee pool in cents. Anything unparseable or negative is 0. */
export function accruedFeeCents(feesEur: unknown): number {
  const n = Number(feesEur);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100);
}

export type OwedWithFees = {
  /** Rent still open on the month. */
  rentCents: number;
  /** Mahngebühren shown on this month — the whole pool, or 0. */
  feeCents: number;
  /** `rentCents + feeCents`. */
  totalCents: number;
};

/**
 * Rent still open on a month plus the fee pool when this month carries it.
 * A month that does not carry the pool reports `feeCents: 0`, so summing rows
 * can never count the fees twice.
 */
export function owedWithFees(input: {
  rentOpenCents: number;
  feesEur: unknown;
  carriesFees: boolean;
}): OwedWithFees {
  const rentCents = Math.max(0, Math.round(Number(input.rentOpenCents) || 0));
  const feeCents = input.carriesFees ? accruedFeeCents(input.feesEur) : 0;
  return { rentCents, feeCents, totalCents: rentCents + feeCents };
}

/**
 * Which open rent row carries the fee pool — the same rule `placeBookingFees`
 * applies on the reminders list, so both screens put the fees on one month:
 *
 *  - the CURRENT billing period's row while it is overdue by the dunning
 *    ladder's own measure (`computeCurrentStage(computeDaysOverdue(dueDay)) >
 *    0`, exactly what the reminders list keys on), else
 *  - the oldest open month that is already due.
 *
 * A month that is not due yet never carries fees; with nothing open and due,
 * the result is null and the pool stands on its own (rent settled, fees not).
 * That is the reminders list's case too: its current-period row is then the
 * booking's own settled row showing EUR 0 rent plus the pool — the same figure
 * the standalone fee line shows here.
 *
 * `openRows` must already exclude settled and canceled rows. Dates are
 * compared as `YYYY-MM-DD` strings, like every other `due_date` check here.
 */
export function feeCarrierPaymentId(input: {
  openRows: readonly {
    id: string | number | null | undefined;
    due_date?: string | null;
  }[];
  /** `bookings.rent_due_date` — the day of month rent is due. */
  rentDueDay: number;
  todayIso: string;
  /** Clock for the dunning-stage check; defaults to now. */
  now?: Date;
}): string | null {
  const due = input.openRows
    .filter(
      (r) =>
        r.id != null &&
        !!r.due_date &&
        String(r.due_date).slice(0, 10) <= input.todayIso
    )
    .sort((a, b) =>
      String(a.due_date)
        .slice(0, 10)
        .localeCompare(String(b.due_date).slice(0, 10))
    );
  if (due.length === 0) return null;
  const dueDay = Number(input.rentDueDay) || 1;
  const now = input.now ?? new Date();
  const periodYm = currentPeriodDueDate(dueDay, now).toISOString().slice(0, 7);
  const current = due.find((r) => String(r.due_date).slice(0, 7) === periodYm);
  if (current && computeCurrentStage(computeDaysOverdue(dueDay, now)) > 0)
    return String(current.id);
  return String(due[0].id);
}
